import { useMemo, useState } from "react"
import {
	IconCheckDoubleOutline18 as CheckCheckIcon,
	IconCloudUploadOutline18 as UploadCloudIcon,
} from "nucleo-ui-outline-18"
import { parse } from "papaparse"

import { Button } from "@productlane/ui"

import { HeaderStep } from "../../csv-import/steps/header-step"
import { UploadStep } from "../../csv-import/steps/upload-step"
import { ChangelogColumnStep } from "./changelog-column-step"
import { ChangelogDateStep } from "./changelog-date-step"
import { ChangelogPublishedStep } from "./changelog-published-step"

export function ChangelogCsvUploadWizard() {
	const [step, setStep] = useState(0)
	const [data, setData] = useState<string[][]>([[]])
	const [hasHeader, setHasHeader] = useState(true)

	const [titleIndex, setTitleIndex] = useState(0)
	const [descriptionIndex, setDescriptionIndex] = useState<number>(-1)
	const [imageIndex, setImageIndex] = useState<number>(-1)
	const [dateIndex, setDateIndex] = useState<number>(-1)
	const [publishedIndex, setPublishedIndex] = useState<number>(-1)

	const sortedData = useMemo(() => {
		const result = []

		if (!data[0]) {
			return [[]]
		}

		for (let i = 0; i < data[0].length; i++) {
			const newColumn = []

			for (const row of data) {
				newColumn.push(row[i] ?? "")
			}
			result.push(newColumn)
		}

		return result
	}, [data])

	const handleFileInput = (file: File) => {
		setStep(1)
		parse<string[], File>(file, {
			complete: (result) => {
				setData(result.data)
			},
			skipEmptyLines: true,
		})
	}

	const reset = () => {
		setData([[]])
		setHasHeader(true)
		setTitleIndex(0)
		setDescriptionIndex(-1)
		setImageIndex(-1)
		setDateIndex(-1)
		setPublishedIndex(-1)
		setStep(0)
	}

	return (
		<div>
			{step === 0 && <UploadStep onSuccess={handleFileInput} />}
			{step === 1 && (
				<HeaderStep
					data={data}
					hasHeader={hasHeader}
					cancel={() => setStep(0)}
					select={(hasHeader) => {
						setHasHeader(hasHeader)
						setStep(2)
					}}
				/>
			)}
			{step === 2 && (
				<ChangelogColumnStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					titleIndex={titleIndex}
					setTitleIndex={setTitleIndex}
					descriptionIndex={descriptionIndex}
					setDescriptionIndex={setDescriptionIndex}
					imageIndex={imageIndex}
					setImageIndex={setImageIndex}
					back={() => setStep(1)}
					next={() => setStep(3)}
				/>
			)}
			{step === 3 && (
				<ChangelogDateStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					dateIndex={dateIndex}
					setDateIndex={setDateIndex}
					back={() => setStep(2)}
					next={() => setStep(4)}
				/>
			)}
			{step === 4 && (
				<ChangelogPublishedStep
					data={data}
					hasHeaders={hasHeader}
					sortedData={sortedData}
					titleIndex={titleIndex}
					descriptionIndex={descriptionIndex}
					imageIndex={imageIndex}
					dateIndex={dateIndex}
					publishedIndex={publishedIndex}
					setPublishedIndex={setPublishedIndex}
					back={() => setStep(3)}
					onSuccess={() => setStep(5)}
				/>
			)}

			{step === 5 && (
				<div className="flex items-center justify-center p-8">
					<div className="flex flex-col items-center gap-2">
						<CheckCheckIcon className="h-8 w-8 text-green-500" />
						<div className="text-secondary">
							Changelogs successfully imported
						</div>
						<Button
							className="text-secondary"
							size="sm"
							variant="ghost"
							Icon={UploadCloudIcon}
							onClick={reset}
						>
							Upload another file
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
